// apps/accounts/src/pages/admin/StaffLeaveManagement.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

export default function StaffLeaveManagement() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      const data = await pb.collection('staff_leave_requests').getFullList({
        sort: '+created',
        filter: 'status = "pending"',
        expand: 'staff,staff.user',
      });
      setLeaves(data);
    } catch (err) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  // Approve Leave with Paid/Unpaid toggle
  const handleApprove = async (leaveId, staffId, start, end, isPaid) => {
    try {
      // 1. Update leave request status
      await pb.collection('staff_leave_requests').update(leaveId, {
        status: 'approved',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
        is_paid: isPaid,
      });

      // 2. Mark attendance as "absent" for all days in the leave period
      const startDate = new Date(start);
      const endDate = new Date(end);
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        const existing = await pb.collection('staff_attendance')
          .getFirstListItem(`staff="${staffId}" && date="${dateStr}"`)
          .catch(() => null);

        if (existing) {
          await pb.collection('staff_attendance').update(existing.id, {
            status: 'absent',
            leave_request: leaveId,
          });
        } else {
          await pb.collection('staff_attendance').create({
            staff: staffId,
            date: dateStr,
            status: 'absent',
            leave_request: leaveId,
          });
        }
      }

      toast.success(`Leave approved as ${isPaid ? 'Paid' : 'Unpaid'}`);
      fetchLeaves();
    } catch (err) {
      toast.error('Approval failed: ' + err.message);
    }
  };

  const handleReject = async (leaveId) => {
    try {
      await pb.collection('staff_leave_requests').update(leaveId, { status: 'rejected' });
      toast.success('Leave rejected');
      fetchLeaves();
    } catch (err) {
      toast.error('Rejection failed');
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Leave Requests</h1>
        <Button variant="outline" onClick={fetchLeaves}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {leaves.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            No pending leave requests
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {leaves.map((leave) => {
            const [isPaid, setIsPaid] = useState(true); // default to Paid

            return (
              <Card key={leave.id} className="border-l-4 border-l-amber-400">
                <CardContent className="p-4 space-y-4">
                  {/* Staff Info */}
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-800">
                        {leave.expand?.staff?.expand?.user?.name || 'Unknown Staff'}
                      </p>
                      <p className="text-sm text-slate-500">
                        {leave.leave_type} � {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                      </p>
                      {leave.reason && (
                        <p className="text-sm text-slate-600 mt-1">?? {leave.reason}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Pending
                    </Badge>
                  </div>

                  {/* Paid/Unpaid Toggle & Actions */}
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={isPaid}
                        onChange={(e) => setIsPaid(e.target.checked)}
                        className="w-4 h-4 text-amber-500 rounded border-slate-300 focus:ring-amber-400"
                      />
                      Mark as <span className={isPaid ? 'text-green-600' : 'text-red-500'}>
                        {isPaid ? 'Paid Leave' : 'Unpaid Leave'}
                      </span>
                      <span className="text-xs text-slate-400 ml-1">
                        (Paid = No Salary Deduction)
                      </span>
                    </label>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleApprove(
                          leave.id,
                          leave.staff,
                          leave.start_date,
                          leave.end_date,
                          isPaid
                        )}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReject(leave.id)}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}